/* WHERE THE PANELS ARE.

   A panel is a rectangle somewhere in the world: its size, where its centre
   sits, and which way it faces. One screen has one panel, at the origin in the
   plane z = 0, unrotated — the identity case every expression downstream still
   reads as. A corner screen has a second, folded 90° about their shared edge.

   Its own module, and a leaf, because both scene/cameras.js and
   scene/helpers.js need it and cameras.js already imports eyeVec from helpers —
   so putting these there would close an import cycle. Nothing here reaches for
   the eye or the renderer for exactly that reason; anything that needs the
   viewer (how far off a panel it stands, whether the panel can be shown at all)
   lives in cameras.js, on the far side of that edge. */
import * as THREE from 'three';
import { S } from '../state/store.js';

/** The single screen, and the first panel of a corner. */
export function leftPanelDesc(){
  return { w: S.screen.w, h: S.screen.h,
           pos: new THREE.Vector3(0, 0, 0), quat: new THREE.Quaternion() };
}

/* The second panel of a corner screen: the first panel's plane, folded 90°
   about their shared vertical edge. The hinge is the line x = +S.screen.w/2,
   z = 0 — the first panel's right edge — and `convex` says which way the fold
   goes: away from the viewer for the outside corner of a building, toward them
   for the inside corner of a room.

   Two properties hold, and both fail silently if the signs are wrong, so
   tools/verify.mjs asserts them rather than trusting this comment: the panel's
   own left edge (local x = −w/2) lands exactly ON the hinge, for either fold;
   and its front normal (local +Z) points along world +X when convex, i.e. away
   from the first panel's own front.

   Returns null for an ordinary single screen, which is what every caller
   branches on — so the whole feature stays switched off by absence. */
export function rightPanelDesc(){
  const c = S.cornerPanel;
  if(!c) return null;
  const s = c.convex === false ? -1 : 1;
  return {
    w: c.w, h: S.screen.h,
    pos: new THREE.Vector3(S.screen.w/2, 0, -s * c.w/2),
    quat: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0,1,0), s * Math.PI/2)
  };
}

/* A SCREEN ARRAY comes in two shapes, and this file is the one place that
   turns either into what everything downstream actually needs: a rectangle
   the four corners get marked against (wallOf) and a flat list of panel
   descriptors, one per physical display (panelDescs). Nothing past this file
   — the homography solve, updateScreenCam's off-axis frustum, the export
   loop — reads a.cols or a.screens itself; a "panel" has always been just a
   { w, h, pos, quat }, which is exactly general enough for both.

   GRID: { cols, rows, gap, tile:{w,h}, res:{w,h} } — one pitch, one size,
   laid out by formula. The commonest wall: identical displays, evenly spaced.

   FREE: { mode:'free', screens:[{key,w,h,x,y,resW,resH}, ...] } — placed by
   hand, one size and one resolution each. x/y are the screen's TOP-LEFT
   corner in an arbitrary local frame — arbitrary because nobody measures a
   wall from its own centre; an installer picks a corner and offsets everyone
   else from it, the way a site drawing would. Down is +y, matching that
   drawing convention (and export/contact-sheet.js's existing percentage
   layout for a corner panel). There is no gap field: the space between two
   screens is simply wherever their rectangles do not meet, so it needs no
   name and nothing draws a bezel over it — see rebuildBezels in
   scene/helpers.js, which is a grid-only feature for exactly this reason. */

/** The union of a free layout's rectangles, in its own local frame. Exported
 *  so a UI drawing it (plan-view.js) or laying it out in an export
 *  (contact-sheet.js) can place things in the SAME frame panelDescs() does,
 *  rather than each re-deriving the bounds and risking a rounding
 *  disagreement between what is drawn and what is real. */
export function freeBounds(screens){
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for(const s of screens){
    x0 = Math.min(x0, s.x); y0 = Math.min(y0, s.y);
    x1 = Math.max(x1, s.x + s.w); y1 = Math.max(y1, s.y + s.h);
  }
  return { x0, y0, w: x1 - x0, h: y1 - y0 };
}

/** How many physical screens an array describes, whichever shape it is. One
 *  formula, one place — several UI surfaces need this count, and cols*rows
 *  spelled out again at each of them is how one of them ends up reading
 *  a.cols on a free layout that has no such field. */
export const screenCount = a => a.mode === 'free' ? a.screens.length : a.cols * a.rows;

/**
 * The largest per-screen resolution in the array — the binding constraint for
 * "can this machine encode video at this size", and the number worth naming
 * in a warning about it. A grid has exactly one resolution to report; a free
 * layout may have several, so the biggest is reported: if that one encodes,
 * every smaller screen on the same wall will too.
 */
export function peakScreenRes(a){
  if(a.mode !== 'free') return { w: a.res.w, h: a.res.h };
  return a.screens.reduce((m, s) => (s.resW * s.resH > m.w * m.h) ? { w: s.resW, h: s.resH } : m,
                           { w: a.screens[0].resW, h: a.screens[0].resH });
}

/* AN ARRAY OF SCREENS — the wall the layout adds up to, in metres and pixels.

   The margin is a PHYSICAL GAP between displays, so it is part of the wall's
   own extent: three 1.21 m screens with 20 mm between them span 3.67 m, not
   3.63 m. That is what makes the illusion continue behind the bezels rather
   than jog at every seam — each screen then shows the true window for where it
   actually sits, and the pixels that fall in a gap are simply never output.
   A free layout gets the same property for free: the wall is the bounding box
   of wherever the screens actually are, gaps included, because that is what
   freeBounds measures.

   Pixels are the wall at the PITCH, so the preview keeps the wall's real
   aspect and gap pixels exist only on screen. The export never reads this: it
   renders each tile at its own resolution — a.res for a grid, each screen's
   own resW/resH for a free layout, which is also why a free wall's "pitch"
   here is only ever an approximation: screens may not share one. The largest
   is used, so the preview is never coarser than the sharpest screen on the
   wall actually is.

   Pure arithmetic on the array spec — it does not touch S.screen, because its
   whole job is to say what S.screen should BE. */
export function wallOf(a){
  if(a.mode === 'free'){
    const b = freeBounds(a.screens);
    const pitch = Math.max(1e-6, ...a.screens.map(s => s.resW / Math.max(1e-6, s.w)));
    return { w: b.w, h: b.h,
             resW: Math.max(1, Math.round(b.w * pitch)),
             resH: Math.max(1, Math.round(b.h * pitch)) };
  }
  const w = a.cols * a.tile.w + (a.cols - 1) * a.gap;
  const h = a.rows * a.tile.h + (a.rows - 1) * a.gap;
  const pitch = a.res.w / Math.max(1e-6, a.tile.w);      // px per metre
  return { w, h,
           resW: Math.max(1, Math.round(w * pitch)),
           resH: Math.max(1, Math.round(h * pitch)) };
}

/* EVERY panel of whatever this installation is: N tiles for an array, two for a
   corner, one otherwise. The single-screen case returns exactly what
   leftPanelDesc always did, so the common path is unchanged.

   Tiles are coplanar — same plane, same facing, so the quaternion is the
   identity and only the centre moves. A GRID is laid out from the wall's
   top-left with the gap added between tiles but not outside them, which is
   what makes the union of tiles plus gaps come to exactly the wall wallOf()
   reports. A FREE layout is placed the same way in spirit — each screen's
   local top-left position, translated into the wall's own centred frame —
   but by direct lookup rather than formula, since there is no pitch to walk. */
export function panelDescs(){
  const a = S.screenArray;
  if(a){
    if(a.mode === 'free'){
      const b = freeBounds(a.screens);
      return a.screens.map((s, i) => ({
        w: s.w, h: s.h, row: 0, col: i, key: s.key,
        resW: s.resW, resH: s.resH,
        pos: new THREE.Vector3(
          -b.w/2 + (s.x - b.x0) + s.w/2,
           b.h/2 - (s.y - b.y0) - s.h/2, 0),
        quat: new THREE.Quaternion()
      }));
    }
    const wall = wallOf(a);
    const out = [];
    for(let r = 0; r < a.rows; r++)
      for(let c = 0; c < a.cols; c++)
        out.push({
          w: a.tile.w, h: a.tile.h, row: r, col: c,
          pos: new THREE.Vector3(
            -wall.w/2 + a.tile.w/2 + c * (a.tile.w + a.gap),
             wall.h/2 - a.tile.h/2 - r * (a.tile.h + a.gap), 0),
          quat: new THREE.Quaternion()
        });
    return out;
  }
  const r = rightPanelDesc();
  return r ? [leftPanelDesc(), r] : [leftPanelDesc()];
}

/* The four world corners of a panel, TL TR BR BL in its own frame. Shared by
   the Orbit helpers, the frustum lines and the clip cone so those cannot
   disagree about where a panel is — which, when they do, reads as a rendering
   bug rather than as the geometry mismatch it actually is. */
export function panelCorners(desc){
  const hw = desc.w/2, hh = desc.h/2;
  return [[-hw,hh,0],[hw,hh,0],[hw,-hh,0],[-hw,-hh,0]].map(([x,y,z]) =>
    new THREE.Vector3(x,y,z).applyQuaternion(desc.quat).add(desc.pos));
}
